/**
 * Keyboard (arrow-key) multi-selection helpers for the WMS grids.
 *
 * Extracted from the grid components so the row logic behind TW-39194 can be
 * unit tested (Kendo grids cannot be rendered in jsdom).
 *
 * Bug (TW-39194): Ctrl/Shift + arrow could not extend the selection.
 * The handlers derived the "base" row from `grid.select().first()`, i.e. the
 * FIRST selected row in DOM order. That row never moves while selecting
 * downward, so every Ctrl+Down targeted the same next row and just toggled it
 * on/off; Shift+Down produced an inconsistent range. Ctrl+Up only "worked" by
 * accident, because the first selected row moves up as rows are added above it.
 *
 * Fix (all derived from the LIVE selection each press, so no state can go
 * stale when the selection changes by other means):
 *  - Ctrl / plain: anchor on the selection EDGE in the travel direction and
 *    step one row beyond it, so the acted row is always just outside the block.
 *    Ctrl then always adds a new row (never re-toggles an inside row) and a
 *    plain arrow moves the single selection.
 *  - Shift: the Kendo range is anchored at the original click
 *    (`getLastSelection`), so move the range END opposite the anchor. This lets
 *    the range grow outward and shrink back symmetrically when the direction is
 *    reversed, instead of collapsing to "anchor + neighbour".
 *
 * Map selected `<tr>`s to `dataSource.view()` by dataItem/uid (or unique key),
 * never by DOM `.index()`. Group/detail rows in the tbody would otherwise make
 * the sibling index diverge from the view index.
 */

/**
 * True when the key event originated in a filter, pager, or cell editor.
 * Capture-phase grid handlers must ignore these so arrows still type/navigate
 * inside inputs (Find Orders filter row, inline edit).
 */
export function isEditableKeyboardTarget(target: any): boolean {
    if (!target) {
        return false;
    }
    if (typeof target.closest === "function") {
        if (target.closest("input, textarea, select, [contenteditable='true'], .k-filter-row, .k-grid-pager, .k-animation-container")) {
            return true;
        }
    }
    const tag = target.tagName ? String(target.tagName).toLowerCase() : "";
    return tag === "input" || tag === "textarea" || tag === "select" || !!target.isContentEditable;
}

/**
 * +1 Down, -1 Up, 0 when the event is not an arrow we handle.
 * Accepts native KeyboardEvent or jQuery-wrapped keydown.
 */
export function arrowKeyDirection(e: { keyCode?: number, which?: number, key?: string }): number {
    const code = e.keyCode || e.which;
    if (code === 40 || e.key === "ArrowDown") {
        return 1;
    }
    if (code === 38 || e.key === "ArrowUp") {
        return -1;
    }
    return 0;
}

/**
 * Left/Right. Find Orders is `navigatable: true` with `selectable: "multiple"`
 * (cells). Kendo then treats Left/Right as cell-select, which rewrites the
 * highlighted set and the Find Orders qty summary. Manage Customers / Roles
 * leave `navigatable` off, so those keys are a no-op there. Swallow them on
 * the grid so row multi-select stays vertical-only (TW-39194).
 */
export function isHorizontalArrowKey(e: { keyCode?: number, which?: number, key?: string }): boolean {
    const code = e.keyCode || e.which;
    return code === 37 || code === 39 || e.key === "ArrowLeft" || e.key === "ArrowRight";
}

/**
 * Index of `dataItem` in `view` (`grid.dataSource.view()`).
 * Matches by object identity or Kendo `uid` so group/detail `<tr>`s in the
 * tbody do not throw off the index the way `$(tr).index()` would.
 * Returns -1 when the item is not a data row in the current view.
 */
export function indexOfViewRow(view: any[], dataItem: any): number {
    if (!dataItem || !view || !view.length) {
        return -1;
    }
    for (let i = 0; i < view.length; i++) {
        const row = view[i];
        if (row === dataItem) {
            return i;
        }
        if (row && row.uid && dataItem.uid && row.uid === dataItem.uid) {
            return i;
        }
    }
    return -1;
}

/**
 * Index of the row whose `uniqueKey` field equals `id` (Shift click-anchor).
 * Returns -1 when the id is unset or not on the current page.
 */
export function indexOfViewRowByKey(view: any[], uniqueKey: string, id: any): number {
    if (!view || !view.length || !uniqueKey || id == null || id === -1) {
        return -1;
    }
    for (let i = 0; i < view.length; i++) {
        if (view[i] && view[i][uniqueKey] === id) {
            return i;
        }
    }
    return -1;
}

/**
 * Resolve the base (anchor) row for the next arrow move.
 * All indices are page-relative (indexes into `grid.dataSource.view()`).
 * A negative return means "before the first row" (so Down lands on row 0).
 *
 * @param direction          +1 for Down (keyCode 40), -1 for Up (keyCode 38)
 * @param isShift            Shift held (anchored range extension)
 * @param firstSelectedIndex lowest selected row index on the page, -1 if none
 * @param lastSelectedIndex  highest selected row index on the page, -1 if none
 * @param anchorIndex        Shift range anchor row on this page, -1 if unknown
 * @param activeRowIndex     active cell's row index, -1 if none
 */
export function resolveArrowBaseRow(
    direction: number,
    isShift: boolean,
    firstSelectedIndex: number,
    lastSelectedIndex: number,
    anchorIndex: number,
    activeRowIndex: number
): number {
    // Shift: move the range END opposite the anchor (the "moving end"), so the
    // anchored Kendo range grows and shrinks symmetrically on reversal.
    if (isShift && firstSelectedIndex >= 0 && lastSelectedIndex >= 0 && anchorIndex >= 0) {
        const distToLast = Math.abs(lastSelectedIndex - anchorIndex);
        const distToFirst = Math.abs(anchorIndex - firstSelectedIndex);
        return distToLast >= distToFirst ? lastSelectedIndex : firstSelectedIndex;
    }

    // Ctrl / plain (and Shift fallback): the selection edge in the travel direction.
    const edge = direction === 1 ? lastSelectedIndex : firstSelectedIndex;
    if (edge >= 0) {
        return edge;
    }
    if (activeRowIndex >= 0) {
        return activeRowIndex;
    }
    // Nothing selected/active: first Down selects row 0, first Up the top row.
    return direction === 1 ? -1 : 0;
}

/**
 * Compute the next page-relative row index for an arrow move.
 * Returns -1 when the move would leave the current page (caller aborts).
 *
 * @param baseRow   base/anchor row from {@link resolveArrowBaseRow}
 * @param direction +1 for Down (keyCode 40), -1 for Up (keyCode 38)
 * @param rowCount  number of rows in the current page view
 */
export function getNextArrowNavRow(baseRow: number, direction: number, rowCount: number): number {
    if (!rowCount || rowCount <= 0) {
        return -1;
    }
    const next = baseRow + direction;
    if (next < 0 || next >= rowCount) {
        return -1;
    }
    return next;
}

/**
 * Inclusive range of view rows from startId to endId (either order).
 * Used for Shift+arrow when there is no GridSelections URL (e.g. Manage Roles)
 * and for virtual in-memory Shift. Returns [] if either id is missing.
 */
export function sliceViewRangeByKey(view: any[], uniqueKey: string, startId: any, endId: any): any[] {
    if (!view || !view.length || !uniqueKey || startId == null || endId == null) {
        return [];
    }
    let startIndex = -1;
    let endIndex = -1;
    for (let i = 0; i < view.length; i++) {
        const row = view[i];
        if (!row) {
            continue;
        }
        if (row[uniqueKey] == startId) {
            startIndex = i;
        }
        if (row[uniqueKey] == endId) {
            endIndex = i;
        }
    }
    if (startIndex < 0 || endIndex < 0) {
        return [];
    }
    const from = Math.min(startIndex, endIndex);
    const to = Math.max(startIndex, endIndex);
    return view.slice(from, to + 1);
}

/**
 * Inclusive slice of the current page view by indexes (keyboard Shift range).
 * One keypress moves `toIndex` by one; the block is always from the click
 * anchor through that index — never an id-range across the full dataset.
 */
export function sliceViewByIndexRange(view: any[], fromIndex: number, toIndex: number): any[] {
    if (!view || !view.length || fromIndex < 0 || toIndex < 0) {
        return [];
    }
    const from = Math.min(fromIndex, toIndex);
    const to = Math.max(fromIndex, toIndex);
    return view.slice(from, to + 1);
}

/**
 * Min/max view indexes of selected keys that are present on the current page.
 * Virtual grids keep selection in a KeyedCollection while `grid.select()` only
 * sees rendered rows — using the keys matches Manage Roles / Customers Shift.
 */
export function viewIndexRangeForKeys(view: any[], uniqueKey: string, keys: any[]): { first: number, last: number } {
    let first = -1;
    let last = -1;
    if (!keys || !keys.length) {
        return { first, last };
    }
    for (let i = 0; i < keys.length; i++) {
        const idx = indexOfViewRowByKey(view, uniqueKey, keys[i]);
        if (idx < 0) {
            continue;
        }
        if (first < 0 || idx < first) {
            first = idx;
        }
        if (idx > last) {
            last = idx;
        }
    }
    return { first, last };
}
